import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import openNotification from '../../components/Common/OpenNotification';
import {
    CUSTOMER_GROUP_MASTER,
    CUSTOMER_GROUP_MASTER_DOWNLOAD_TEMPLATE,
    CUSTOMER_MASTER,
    CUSTOMER_MASTER_DOWNLOAD_TEMPLATE,
    CPC_MASTER,
    CPC_MASTER_DOWNLOAD_TEMPLATE,
} from '../../api/endpoints';

const initialState = {
    customerGroupMasterList: [],
    customerGroupMasterPagination: {},
    gettingCustomerGroupMasterList: false,
    uploadingCustomerGroupMaster: false,
    downloadingCustomerGroupMasterTemplate: false,

    customerMasterList: [],
    customerMasterPagination: {},
    gettingCustomerMasterList: false,
    uploadingCustomerMaster: false,
    downloadingCustomerMasterTemplate: false,

    cpcMasterList: [],
    cpcMasterPagination: {},
    gettingCpcMasterList: false,
    uploadingCpcMaster: false,
    downloadingCpcMasterTemplate: false,
};

const downloadBlob = (data, mime, fileName) => {
    const blob = new Blob([data], { type: mime });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// -------------------- Customer Group Master --------------------

export const getCustomerGroupMasterList = createAsyncThunk(
    'masterUploads/getCustomerGroupMasterList',
    async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
        try {
            const params = { page, limit };
            if (search) params.search = search;
            const response = await axiosInstance.get(CUSTOMER_GROUP_MASTER, { params });
            return response.data;
        } catch (error) {
            openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const uploadCustomerGroupMaster = createAsyncThunk(
    'masterUploads/uploadCustomerGroupMaster',
    async ({ data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(CUSTOMER_GROUP_MASTER, { data });
            openNotification('success', 'Upload Successful', response.data.message);
            return response.data;
        } catch (error) {
            openNotification('error', 'Upload Failed', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const downloadCustomerGroupMasterTemplate = createAsyncThunk(
    'masterUploads/downloadCustomerGroupMasterTemplate',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                CUSTOMER_GROUP_MASTER_DOWNLOAD_TEMPLATE,
                {},
                { responseType: 'blob' },
            );
            downloadBlob(response.data, response?.data?.type || XLSX_MIME, 'Customer_Group_Master_Template.xlsx');
            return true;
        } catch (error) {
            openNotification('error', 'Download Failed', error?.message || 'Something went wrong.');
            return rejectWithValue(error?.message || 'Something went wrong.');
        }
    },
);

// -------------------- Customer Master --------------------

export const getCustomerMasterList = createAsyncThunk(
    'masterUploads/getCustomerMasterList',
    async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
        try {
            const params = { page, limit };
            if (search) params.search = search;
            const response = await axiosInstance.get(CUSTOMER_MASTER, { params });
            return response.data;
        } catch (error) {
            openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const uploadCustomerMaster = createAsyncThunk(
    'masterUploads/uploadCustomerMaster',
    async ({ data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(CUSTOMER_MASTER, { data });
            openNotification('success', 'Upload Successful', response.data.message);
            return response.data;
        } catch (error) {
            openNotification('error', 'Upload Failed', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const downloadCustomerMasterTemplate = createAsyncThunk(
    'masterUploads/downloadCustomerMasterTemplate',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                CUSTOMER_MASTER_DOWNLOAD_TEMPLATE,
                {},
                { responseType: 'blob' },
            );
            downloadBlob(response.data, response?.data?.type || XLSX_MIME, 'Customer_Master_Template.xlsx');
            return true;
        } catch (error) {
            openNotification('error', 'Download Failed', error?.message || 'Something went wrong.');
            return rejectWithValue(error?.message || 'Something went wrong.');
        }
    },
);

// -------------------- CPC Master --------------------

export const getCpcMasterList = createAsyncThunk(
    'masterUploads/getCpcMasterList',
    async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
        try {
            const params = { page, limit };
            if (search) params.search = search;
            const response = await axiosInstance.get(CPC_MASTER, { params });
            return response.data;
        } catch (error) {
            openNotification('error', 'Error', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const uploadCpcMaster = createAsyncThunk(
    'masterUploads/uploadCpcMaster',
    async ({ data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(CPC_MASTER, { data });
            openNotification('success', 'Upload Successful', response.data.message);
            return response.data;
        } catch (error) {
            openNotification('error', 'Upload Failed', error?.response?.data?.message || 'Something went wrong.');
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
    },
);

export const downloadCpcMasterTemplate = createAsyncThunk(
    'masterUploads/downloadCpcMasterTemplate',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                CPC_MASTER_DOWNLOAD_TEMPLATE,
                {},
                { responseType: 'blob' },
            );
            downloadBlob(response.data, response?.data?.type || XLSX_MIME, 'CPC_Master_Template.xlsx');
            return true;
        } catch (error) {
            openNotification('error', 'Download Failed', error?.message || 'Something went wrong.');
            return rejectWithValue(error?.message || 'Something went wrong.');
        }
    },
);

const masterUploadsSlice = createSlice({
    name: 'masterUploads',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Customer Group Master
            .addCase(getCustomerGroupMasterList.pending, (state) => {
                state.gettingCustomerGroupMasterList = true;
            })
            .addCase(getCustomerGroupMasterList.fulfilled, (state, action) => {
                state.gettingCustomerGroupMasterList = false;
                state.customerGroupMasterList = action.payload.data?.data || [];
                state.customerGroupMasterPagination = action.payload.data?.pagination || {};
            })
            .addCase(getCustomerGroupMasterList.rejected, (state) => {
                state.gettingCustomerGroupMasterList = false;
            })
            .addCase(uploadCustomerGroupMaster.pending, (state) => {
                state.uploadingCustomerGroupMaster = true;
            })
            .addCase(uploadCustomerGroupMaster.fulfilled, (state) => {
                state.uploadingCustomerGroupMaster = false;
            })
            .addCase(uploadCustomerGroupMaster.rejected, (state) => {
                state.uploadingCustomerGroupMaster = false;
            })
            .addCase(downloadCustomerGroupMasterTemplate.pending, (state) => {
                state.downloadingCustomerGroupMasterTemplate = true;
            })
            .addCase(downloadCustomerGroupMasterTemplate.fulfilled, (state) => {
                state.downloadingCustomerGroupMasterTemplate = false;
            })
            .addCase(downloadCustomerGroupMasterTemplate.rejected, (state) => {
                state.downloadingCustomerGroupMasterTemplate = false;
            })

            // Customer Master
            .addCase(getCustomerMasterList.pending, (state) => {
                state.gettingCustomerMasterList = true;
            })
            .addCase(getCustomerMasterList.fulfilled, (state, action) => {
                state.gettingCustomerMasterList = false;
                state.customerMasterList = action.payload.data?.data || [];
                state.customerMasterPagination = action.payload.data?.pagination || {};
            })
            .addCase(getCustomerMasterList.rejected, (state) => {
                state.gettingCustomerMasterList = false;
            })
            .addCase(uploadCustomerMaster.pending, (state) => {
                state.uploadingCustomerMaster = true;
            })
            .addCase(uploadCustomerMaster.fulfilled, (state) => {
                state.uploadingCustomerMaster = false;
            })
            .addCase(uploadCustomerMaster.rejected, (state) => {
                state.uploadingCustomerMaster = false;
            })
            .addCase(downloadCustomerMasterTemplate.pending, (state) => {
                state.downloadingCustomerMasterTemplate = true;
            })
            .addCase(downloadCustomerMasterTemplate.fulfilled, (state) => {
                state.downloadingCustomerMasterTemplate = false;
            })
            .addCase(downloadCustomerMasterTemplate.rejected, (state) => {
                state.downloadingCustomerMasterTemplate = false;
            })

            // CPC Master
            .addCase(getCpcMasterList.pending, (state) => {
                state.gettingCpcMasterList = true;
            })
            .addCase(getCpcMasterList.fulfilled, (state, action) => {
                state.gettingCpcMasterList = false;
                state.cpcMasterList = action.payload.data?.data || [];
                state.cpcMasterPagination = action.payload.data?.pagination || {};
            })
            .addCase(getCpcMasterList.rejected, (state) => {
                state.gettingCpcMasterList = false;
            })
            .addCase(uploadCpcMaster.pending, (state) => {
                state.uploadingCpcMaster = true;
            })
            .addCase(uploadCpcMaster.fulfilled, (state) => {
                state.uploadingCpcMaster = false;
            })
            .addCase(uploadCpcMaster.rejected, (state) => {
                state.uploadingCpcMaster = false;
            })
            .addCase(downloadCpcMasterTemplate.pending, (state) => {
                state.downloadingCpcMasterTemplate = true;
            })
            .addCase(downloadCpcMasterTemplate.fulfilled, (state) => {
                state.downloadingCpcMasterTemplate = false;
            })
            .addCase(downloadCpcMasterTemplate.rejected, (state) => {
                state.downloadingCpcMasterTemplate = false;
            });
    },
});

export default masterUploadsSlice.reducer;
